import type { Key } from "react";
import type React from "react";
import type { ColumnsType, TableRowSelection } from "antd/es/table/interface";

import DashboardFetchWrapper from "./DashboardFetchWrapper";
import { DashboardTemplate } from "./DashboardTemplate";
import type { DashboardTableContext, ExtraTab } from "./types";
import type { FilterConditionValue } from "@/components/UI/TableToolbar/Filtering/filterTypes";
import { getWorkspaceDataSection } from "./workspaceDataSection";

type DashboardTemplateProps = {
  type: string;
  constants: {
    wikiLink: string;
    idLink: string | undefined | ((subjectId: string) => string);
  };
  subjectId?: string;
  /**
   * Scopes the screen to a single site — used by the site-profile subviews.
   * Applied as a fixed `site` filter on the metadata, table, download and plot
   * requests.
   */
  siteId?: string;
  extraTabs?: ExtraTab[];
  screenViewUrlParam?: string;
  extraColumns?: ColumnsType<Record<string, unknown>>;
  selectionIdentifierColumn?: string;
  endpointPrefix?: string;
  /** Fixed filters merged into every table request (e.g. exclude a modality). */
  presetFilters?: Record<string, FilterConditionValue>;
  rowKey?: string | ((record: Record<string, unknown>) => Key);
  onRow?: (
    record: Record<string, unknown>,
  ) => React.HTMLAttributes<HTMLElement>;
  dataPageNameField?: string;
  pinnedFilters?: Record<string, string[]>;
  /** Multi-select, keyed on the caller's own `rowKey` values. */
  rowSelection?: TableRowSelection<Record<string, unknown>>;
  /** Reports what the table is showing, for screens that own row selection. */
  onTableContextChange?: (context: DashboardTableContext) => void;
};

export const MetadataDashboardTemplate = ({
  type,
  constants,
  subjectId,
  siteId,
  extraTabs = [],
  screenViewUrlParam,
  extraColumns = [],
  selectionIdentifierColumn = "subject",
  endpointPrefix,
  presetFilters,
  rowKey,
  onRow,
  dataPageNameField,
  pinnedFilters,
  rowSelection,
  onTableContextChange,
}: DashboardTemplateProps) => {
  // Remount on scope change so table state (filters, paging) never leaks from
  // one subject or site to the next.
  const key = [type, subjectId, siteId].filter(Boolean).join("-");

  return (
    <DashboardFetchWrapper
      key={key}
      datasetName={type}
      subjectId={subjectId}
      siteId={siteId}
      endpointPrefix={endpointPrefix}
      dataSection={getWorkspaceDataSection(dataPageNameField, pinnedFilters)}
    >
      {({ metadata, workspace }) => (
        <DashboardTemplate
          metadata={metadata}
          datasetName={type}
          constants={constants}
          subjectId={subjectId}
          siteId={siteId}
          workspace={workspace}
          extraTabs={extraTabs}
          screenViewUrlParam={screenViewUrlParam}
          extraColumns={extraColumns}
          selectionIdentifierColumn={selectionIdentifierColumn}
          endpointPrefix={endpointPrefix}
          presetFilters={presetFilters}
          rowKey={rowKey}
          onRow={onRow}
          dataPageNameField={dataPageNameField}
          pinnedFilters={pinnedFilters}
          rowSelection={rowSelection}
          onTableContextChange={onTableContextChange}
        />
      )}
    </DashboardFetchWrapper>
  );
};
